import { type User } from '../types'
import api from './api'

export interface AuthTokens {
  accessToken: string
  client: string
  uid: string
}

export const authService = {
  async login(email: string, password: string): Promise<User> {
    const response = await api.post('/auth/sign_in', { email, password })
    return response.data.data
  },

  async register(
    email: string,
    password: string,
    passwordConfirmation: string,
    turnstileToken?: string
  ): Promise<User> {
    const response = await api.post('/auth', {
      email,
      password,
      password_confirmation: passwordConfirmation,
      turnstile_token: turnstileToken,
    })
    return response.data.data
  },

  async logout(): Promise<void> {
    try {
      await api.delete('/auth/sign_out')
    } finally {
      this.clearTokens()
    }
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/password', {
      email,
      redirect_url: `${window.location.origin}/reset-password`,
    })
  },

  async resetPassword(
    password: string,
    passwordConfirmation: string,
    resetToken: string
  ): Promise<void> {
    await api.put('/auth/password', {
      password,
      password_confirmation: passwordConfirmation,
      reset_password_token: resetToken,
    })
  },

  async validateToken(): Promise<User> {
    const response = await api.get('/me')
    return response.data.user
  },

  getStoredTokens(): AuthTokens | null {
    const accessToken = localStorage.getItem('access-token')
    const client = localStorage.getItem('client')
    const uid = localStorage.getItem('uid')

    if (accessToken && client && uid) {
      return { accessToken, client, uid }
    }
    return null
  },

  clearTokens(): void {
    localStorage.removeItem('access-token')
    localStorage.removeItem('client')
    localStorage.removeItem('uid')
  },

  storeTokens(tokens: {
    'access-token': string
    client: string
    uid: string
    expiry?: string
  }): void {
    localStorage.setItem('access-token', tokens['access-token'])
    localStorage.setItem('client', tokens.client)
    localStorage.setItem('uid', tokens.uid)
    if (tokens.expiry) {
      localStorage.setItem('expiry', tokens.expiry)
    }
  },

  async loginWithTokens(tokens: {
    'access-token': string
    client: string
    uid: string
    expiry?: string
  }): Promise<User> {
    this.storeTokens(tokens)
    return this.validateToken()
  },

  async googleLogin(credential: string): Promise<User> {
    const response = await api.post('/auth/google', { credential })

    // Store tokens from response headers
    const accessToken = response.headers['access-token']
    const client = response.headers['client']
    const uid = response.headers['uid']
    const expiry = response.headers['expiry']

    if (accessToken && client && uid) {
      this.storeTokens({
        'access-token': accessToken,
        client,
        uid,
        expiry,
      })
    }

    return response.data.data
  },

  async changePassword(
    currentPassword: string,
    newPassword: string,
    newPasswordConfirmation: string
  ): Promise<void> {
    await api.put('/auth', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPasswordConfirmation,
    })
  },

  async deleteAccount(): Promise<void> {
    await api.delete('/auth')
    this.clearTokens()
  },
}
